import { paceOf } from '@painpal/core';
import { sportSchema, type ActivityPage, type ActivitySummary, type Sport } from '@painpal/contracts';
import type { SportFilter } from '../../api/keys';
import { formatDuration, formatKm, formatPaceKm, formatWatts, PROCESSING_LABEL } from '../../lib/format';
import { plural } from '../../lib/plural';
import { sourceSportLabel } from '../activity/runDetailModel';

export const SPORT_FILTERS: readonly SportFilter[] = ['all', ...sportSchema.options];

export function sportSearch(sport: SportFilter): { sport?: Sport } {
  return sport === 'all' ? {} : { sport };
}

export function countsEyebrow(page: Pick<ActivityPage, 'total' | 'withLoad'> | undefined): string {
  if (page === undefined) return 'LOADING ACTIVITIES';
  return `${page.total} ${plural(page.total, 'ACTIVITY', 'ACTIVITIES')} · ${page.withLoad} WITH LOAD · ${page.total - page.withLoad} WITHOUT`;
}

export function pageTotal(pages: Pick<ActivityPage, 'total'>[]): number {
  return Math.max(0, ...pages.map((page) => page.total));
}

export function pendingLabel(activity: ActivitySummary, recalcFrom: string | null): string {
  return recalcFrom !== null && activity.localDate >= recalcFrom ? 'RECALCULATING' : PROCESSING_LABEL;
}

const avgHr = (a: ActivitySummary): string => (a.avgHr === null ? '—' : `${Math.round(a.avgHr)} BPM AVG`);

// Weighted Power for rides, average moving pace for runs that have a distance, average HR for everything else.
export function keyMetric(a: ActivitySummary): string {
  if (a.sport === 'ride') return a.weightedPower === null ? avgHr(a) : formatWatts(a.weightedPower);
  if (a.sport === 'run' && a.distanceM !== null && a.distanceM > 0 && a.movingS > 0) {
    return formatPaceKm(paceOf(a.distanceM / a.movingS));
  }
  return avgHr(a);
}

// The Strava type says what a strength or other session was; a run whose Load came from HR had no usable pace.
export function subType(a: ActivitySummary): string | null {
  if (a.sport === 'run') return a.loadMethod === 'hr' ? 'NO PACE STREAM' : null;
  if ((a.sport === 'strength' || a.sport === 'other') && a.source === 'strava') return sourceSportLabel(a.sourceSport);
  return null;
}

export function metaLine(a: ActivitySummary): string {
  const metric = keyMetric(a);
  const type = subType(a);
  const parts = [formatDuration(a.durationS), a.distanceM === null ? null : formatKm(a.distanceM), metric === '—' ? null : metric];
  if (metric === '—' && type !== null) parts.push(`STRAVA: ${type}`);
  return parts.filter((p): p is string => p !== null).join(' · ');
}
